import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useBusinessProfiles } from '@/hooks/useBusinessProfiles';
import { useAuth } from '@/context/AuthContext';
import { useCreditsContext } from '@/context/CreditsContext';
import { generateSocialPost, SocialPostInsufficientCreditsError } from '@/api/generateSocialPost';
import { useSocialPostHistory, historyItemToResult, historyItemToDailyResult } from '@/hooks/useSocialPostHistory';
import { useCreativePresets, type CreativePreset, type SocialPostPresetSettings } from '@/hooks/useCreativePresets';
import type { SocialPostGeneration } from '@/hooks/useSocialPostHistory';
import type {
  SocialPostFormData,
  SocialPostResult,
  DailyContentFormData,
  DailyContentResult,
} from './types';
import { SOCIAL_POST_COST, SOCIAL_IMAGE_COST } from './types';
import SocialPostForm from './components/SocialPostForm';
import SocialPostResults from './components/SocialPostResults';
import SocialPostHistoryPanel from './components/SocialPostHistoryPanel';
import DailyContentForm from './components/DailyContentForm';
import DailyContentResults from './components/DailyContentResults';

type PostMode = 'campaign' | 'daily';

const defaultCampaignForm: SocialPostFormData = {
  identityType: 'business',
  businessName: '',
  creatorNiche: '',
  creatorPersonality: '',
  contentPillars: '',
  creatorAudienceType: '',
  creatorTone: '',
  contentStyle: '',
  platform: 'Instagram',
  postType: 'Daily Promo',
  goal: '',
  offerTopic: '',
  targetAudience: '',
  targetAudienceType: '',
  brandVoice: 'Professional',
  imageStyle: '',
  creativeDirection: '',
  captionLength: 'Medium',
  includeImage: true,
  cta: '',
  hashtags: '',
  specialInstructions: '',
  theme: '',
  eventPromotion: '',
  referenceStyle: '',
  autoMode: false,
  referenceImageBase64: null,
  referenceImagePrompt: '',
};

const defaultDailyForm: DailyContentFormData = {
  identityType: 'business',
  businessName: '',
  creatorNiche: '',
  creatorPersonality: '',
  contentPillars: '',
  creatorAudienceType: '',
  creatorTone: '',
  contentStyle: '',
  platform: 'Instagram',
  contentPersonality: '',
  audienceFocus: '',
  location: '',
  dailyGoal: '',
  contentThemes: [],
  avoidRepetition: true,
  quantity: '1',
};

export default function SocialPostPage() {
  const location = useLocation();
  const { user } = useAuth();
  const { profiles, loading: profilesLoading } = useBusinessProfiles();
  const { creditsRemaining, hasEnoughCredits, refetch: refetchCredits, fireToast } = useCreditsContext();
  const { history, loading: historyLoading, savePost, deletePost } = useSocialPostHistory();
  const {
    presets: socialPresets,
    loading: presetsLoading,
    error: presetsError,
    createPreset,
    deletePreset,
  } = useCreativePresets<SocialPostPresetSettings>('social_post');

  const [postMode, setPostMode] = useState<PostMode>('campaign');
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetMessage, setPresetMessage] = useState<string | null>(null);
  const [savingPreset, setSavingPreset] = useState(false);

  // Campaign mode state
  const [campaignForm, setCampaignForm] = useState<SocialPostFormData>(defaultCampaignForm);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [campaignResult, setCampaignResult] = useState<SocialPostResult | null>(null);

  // Derive business context from selected profile
  const selectedProfile = profiles.find((p) => p.id === selectedProfileId) ?? null;

  // Daily content mode state
  const [dailyForm, setDailyForm] = useState<DailyContentFormData>(defaultDailyForm);
  const [dailySelectedProfileId, setDailySelectedProfileId] = useState<string | null>(null);
  const [dailyResult, setDailyResult] = useState<DailyContentResult | null>(null);

  const selectedDailyProfile = profiles.find((p) => p.id === dailySelectedProfileId) ?? null;
  const selectedPreset = socialPresets.find((preset) => preset.id === selectedPresetId);

  // Shared state
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [insufficientCredits, setInsufficientCredits] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [loadedFromHistory, setLoadedFromHistory] = useState(false);
  const generationAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => generationAbortRef.current?.abort();
  }, []);

  const stopGeneration = useCallback(() => {
    generationAbortRef.current?.abort();
    generationAbortRef.current = null;
    setIsGenerating(false);
    setError('Generation stopped. Update your form and try again when you are ready.');
  }, []);

  // ── Campaign generate ──────────────────────────────────────────────────────
  const handleCampaignGenerate = async () => {
    if (isGenerating) return;
    const abortController = new AbortController();
    generationAbortRef.current = abortController;
    setIsGenerating(true);
    setError(null);
    setInsufficientCredits(false);

    try {
      const postResult = await generateSocialPost(
        campaignForm,
        user?.id,
        selectedProfile?.logo_url ?? undefined,
        undefined,
        {
          businessDescription: selectedProfile?.business_description || undefined,
          servicesOffered: selectedProfile?.services_offered || undefined,
          businessType: selectedProfile?.business_type || undefined,
          location: selectedProfile?.location || undefined,
          uniqueDifferentiator: selectedProfile?.unique_differentiator || undefined,
          defaultAudience: selectedProfile?.default_audience || undefined,
          defaultTone: selectedProfile?.default_tone || undefined,
          brandPositioning: selectedProfile?.brand_positioning || undefined,
        },
        { signal: abortController.signal }
      );
      if (abortController.signal.aborted || generationAbortRef.current !== abortController) return;
      setCampaignResult(postResult);
      const cost = SOCIAL_POST_COST + (campaignForm.includeImage ? SOCIAL_IMAGE_COST : 0);
      await refetchCredits();
      fireToast('Social post generated!', 'deduct', cost);
      await savePost(campaignForm, postResult);
    } catch (err) {
      if (abortController.signal.aborted) {
        if (generationAbortRef.current === abortController) {
          setError('Generation stopped. Update your form and try again when you are ready.');
        }
      } else if (err instanceof SocialPostInsufficientCreditsError) {
        setInsufficientCredits(true);
      } else {
        setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      }
    } finally {
      if (generationAbortRef.current === abortController) {
        generationAbortRef.current = null;
        setIsGenerating(false);
      }
    }
  };

  // ── Daily content generate ─────────────────────────────────────────────────
  const handleDailyGenerate = async () => {
    if (isGenerating) return;
    const abortController = new AbortController();
    generationAbortRef.current = abortController;
    setIsGenerating(true);
    setError(null);
    setInsufficientCredits(false);

    try {
      const quantityCost =
        dailyForm.quantity === '7'
          ? SOCIAL_POST_COST * 3
          : dailyForm.quantity === '3'
          ? SOCIAL_POST_COST * 2
          : SOCIAL_POST_COST;

      const result = await generateSocialPost(
        {
          ...defaultCampaignForm,
          businessName: dailyForm.businessName,
          identityType: dailyForm.identityType,
          creatorNiche: dailyForm.creatorNiche,
          creatorPersonality: dailyForm.creatorPersonality,
          contentPillars: dailyForm.contentPillars,
          creatorAudienceType: dailyForm.creatorAudienceType,
          creatorTone: dailyForm.creatorTone,
          contentStyle: dailyForm.contentStyle,
          platform: dailyForm.platform,
          postType: 'Daily Content',
          goal: dailyForm.dailyGoal,
          offerTopic: '',
          targetAudience: dailyForm.audienceFocus,
          brandVoice: dailyForm.contentPersonality,
          specialInstructions: '',
          includeImage: false,
          autoMode: false,
        },
        user?.id,
        undefined,
        {
          mode: 'daily',
          quantity: dailyForm.quantity,
          contentPersonality: dailyForm.contentPersonality,
          audienceFocus: dailyForm.audienceFocus,
          location: dailyForm.location,
          dailyGoal: dailyForm.dailyGoal,
          contentThemes: dailyForm.contentThemes,
          avoidRepetition: dailyForm.avoidRepetition,
        },
        {
          businessDescription: selectedDailyProfile?.business_description || undefined,
          servicesOffered: selectedDailyProfile?.services_offered || undefined,
          businessType: selectedDailyProfile?.business_type || undefined,
          location: selectedDailyProfile?.location || undefined,
          uniqueDifferentiator: selectedDailyProfile?.unique_differentiator || undefined,
          defaultAudience: selectedDailyProfile?.default_audience || undefined,
          defaultTone: selectedDailyProfile?.default_tone || undefined,
          brandPositioning: selectedDailyProfile?.brand_positioning || undefined,
        },
        { signal: abortController.signal }
      );
      if (abortController.signal.aborted || generationAbortRef.current !== abortController) return;

      // The edge function returns a DailyContentResult when mode=daily
      const dailyRes = result as unknown as DailyContentResult;
      setDailyResult({
        type: 'daily_content',
        quantity: dailyForm.quantity,
        platform: dailyForm.platform,
        businessName: dailyForm.businessName,
        posts: dailyRes.posts ?? [],
      });

      await refetchCredits();
      fireToast('Daily content generated!', 'deduct', quantityCost);
      await savePost(dailyForm, {
        type: 'daily_content',
        quantity: dailyForm.quantity,
        platform: dailyForm.platform,
        businessName: dailyForm.businessName,
        posts: dailyRes.posts ?? [],
      });
    } catch (err) {
      if (abortController.signal.aborted) {
        if (generationAbortRef.current === abortController) {
          setError('Generation stopped. Update your form and try again when you are ready.');
        }
      } else if (err instanceof SocialPostInsufficientCreditsError) {
        setInsufficientCredits(true);
      } else {
        setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      }
    } finally {
      if (generationAbortRef.current === abortController) {
        generationAbortRef.current = null;
        setIsGenerating(false);
      }
    }
  };

  const handleLoadFromHistory = (item: SocialPostGeneration) => {
    if (item.generation_type === 'daily_content') {
      const loadedDailyResult = historyItemToDailyResult(item);
      setDailyResult(loadedDailyResult);
      setDailyForm((prev) => ({
        ...prev,
        businessName: item.business_name ?? prev.businessName,
        platform: item.platform ?? prev.platform,
        contentPersonality: item.content_personality ?? prev.contentPersonality,
        audienceFocus: item.audience_focus ?? prev.audienceFocus,
        location: item.location_context ?? prev.location,
        dailyGoal: item.goal ?? prev.dailyGoal,
        contentThemes: item.content_themes ?? prev.contentThemes,
        quantity: (item.plan_quantity as DailyContentFormData['quantity']) ?? prev.quantity,
      }));
      setPostMode('daily');
      setShowHistory(false);
      setLoadedFromHistory(true);
      return;
    }

    const loadedResult = historyItemToResult(item);
    setCampaignResult(loadedResult);
    setCampaignForm((prev) => ({
      ...prev,
      businessName: item.business_name ?? prev.businessName,
      platform: item.platform ?? prev.platform,
      postType: item.post_type ?? prev.postType,
      goal: item.goal ?? prev.goal,
      offerTopic: item.offer_topic ?? prev.offerTopic,
      targetAudience: item.target_audience ?? prev.targetAudience,
      targetAudienceType: '',
      brandVoice: item.brand_voice ?? prev.brandVoice,
      imageStyle: '',
      theme: '',
      eventPromotion: '',
      referenceStyle: '',
      captionLength: (item.caption_length as SocialPostFormData['captionLength']) ?? prev.captionLength,
      includeImage: item.include_image ?? prev.includeImage,
      autoMode: false,
      referenceImageBase64: null,
      referenceImagePrompt: '',
    }));
    setPostMode('campaign');
    setShowHistory(false);
    setLoadedFromHistory(true);
  };

  // Handle incoming history item from history page navigation
  useEffect(() => {
    const state = location.state as { historyItem?: SocialPostGeneration } | null;
    const historyItem = state?.historyItem;
    if (historyItem) {
      handleLoadFromHistory(historyItem);
      // Clear navigation state so a page refresh doesn't reload the item
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location.pathname, location.state]);

  const handleModeSwitch = (mode: PostMode) => {
    setPostMode(mode);
    setError(null);
    setInsufficientCredits(false);
  };

  const buildPresetSettings = (): SocialPostPresetSettings => ({
    postMode,
    campaignForm: campaignForm as unknown as Record<string, unknown>,
    dailyForm: dailyForm as unknown as Record<string, unknown>,
    selectedProfileId,
    dailySelectedProfileId,
  });

  const applyPreset = (preset: CreativePreset<SocialPostPresetSettings>) => {
    const settings = preset.settings;
    setPostMode(settings.postMode);
    if (settings.campaignForm) setCampaignForm({ ...defaultCampaignForm, ...(settings.campaignForm as Partial<SocialPostFormData>) });
    if (settings.dailyForm) setDailyForm({ ...defaultDailyForm, ...(settings.dailyForm as Partial<DailyContentFormData>) });
    if (settings.selectedProfileId && profiles.some((profile) => profile.id === settings.selectedProfileId)) {
      setSelectedProfileId(settings.selectedProfileId);
    }
    if (settings.dailySelectedProfileId && profiles.some((profile) => profile.id === settings.dailySelectedProfileId)) {
      setDailySelectedProfileId(settings.dailySelectedProfileId);
    }
    setPresetMessage(`Loaded "${preset.name}".`);
  };

  const savePreset = async () => {
    if (savingPreset) return;
    const cleanName = presetName.trim();
    if (!cleanName) {
      setPresetMessage('Name the preset before saving it.');
      return;
    }
    setSavingPreset(true);
    setPresetMessage(null);
    const { error: saveErr } = await createPreset({
      name: cleanName,
      businessProfileId: postMode === 'campaign' ? selectedProfileId : dailySelectedProfileId,
      settings: buildPresetSettings(),
    });
    setSavingPreset(false);
    if (saveErr) {
      setPresetMessage(saveErr);
      return;
    }
    setPresetName('');
    setShowSavePreset(false);
    setPresetMessage(`Saved "${cleanName}" as a reusable preset.`);
  };

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <header className="bg-white border-b border-stone-100 flex items-center justify-between px-4 md:px-6 h-14 md:h-16 shrink-0 sticky top-0 z-10 gap-2">
        <div className="min-w-0 shrink">
          <h1 className="text-sm md:text-base font-bold text-stone-900 truncate">
            Social Post Generator
          </h1>
          <p className="text-xs text-stone-400 hidden sm:block">
            Create organic social content for any platform
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* History toggle */}
          <button
            onClick={() => setShowHistory((v) => !v)}
            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border transition-all duration-200 cursor-pointer whitespace-nowrap ${
              showHistory
                ? 'bg-stone-900 border-stone-900 text-white'
                : 'text-stone-600 bg-stone-100 hover:bg-stone-200 border-stone-200'
            }`}
          >
            <i className="ri-history-line text-sm" />
            <span className="hidden sm:inline">History</span>
            {history.length > 0 && (
              <span
                className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-xs font-bold leading-none ${
                  showHistory ? 'bg-white text-stone-900' : 'bg-orange-500 text-white'
                }`}
              >
                {history.length > 99 ? '99+' : history.length}
              </span>
            )}
          </button>

          <Link
            to="/dashboard/generate"
            className="flex items-center gap-1.5 text-xs font-semibold text-stone-600 bg-stone-100 hover:bg-stone-200 px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-flashlight-fill text-orange-400" />
            <span className="hidden sm:inline">Generate Ads</span>
          </Link>

          {((postMode === 'campaign' && campaignResult) || (postMode === 'daily' && dailyResult)) && !isGenerating && (
            <button
              onClick={() => {
                if (postMode === 'campaign') setCampaignResult(null);
                else setDailyResult(null);
              }}
              className="flex items-center gap-1.5 text-xs font-semibold text-stone-500 bg-stone-100 hover:bg-stone-200 border border-stone-200 px-3 py-1.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-delete-bin-line" />
              <span className="hidden sm:inline">New Post</span>
            </button>
          )}
        </div>
      </header>

      {/* Loaded from history banner */}
      {loadedFromHistory && (campaignResult || dailyResult) && !isGenerating && (
        <div className="mx-4 md:mx-6 mt-4 flex items-center gap-3 bg-teal-50 border border-teal-100 px-4 py-2.5 rounded-xl">
          <i className="ri-history-line text-teal-500 text-sm shrink-0" />
          <p className="text-xs text-stone-600 flex-1">
            <strong className="text-stone-800">Loaded from history.</strong> Edit the form below to regenerate with changes.
          </p>
          <button
            onClick={() => setLoadedFromHistory(false)}
            className="text-xs text-stone-400 hover:text-stone-600 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      )}

      {/* Alerts */}
      {insufficientCredits && (
        <div className="mx-4 md:mx-6 mt-4 flex items-center gap-3 bg-red-50 border border-red-200 px-4 py-3 rounded-xl">
          <div className="w-8 h-8 flex items-center justify-center shrink-0 rounded-full bg-red-100">
            <i className="ri-error-warning-line text-red-500 text-sm" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-stone-900">Not enough credits.</p>
            <p className="text-xs text-stone-500 mt-0.5">
              Please upgrade your plan or purchase more credits.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setInsufficientCredits(false)}
              className="text-stone-400 hover:text-stone-600 cursor-pointer"
            >
              <i className="ri-close-line text-lg" />
            </button>
            <Link
              to="/pricing"
              className="flex items-center gap-1.5 text-xs font-bold bg-gradient-to-r from-orange-500 to-amber-400 text-white px-3 py-1.5 rounded-lg hover:from-orange-600 hover:to-amber-500 transition-all duration-200 whitespace-nowrap"
            >
              <i className="ri-flashlight-fill" /> Upgrade
            </Link>
          </div>
        </div>
      )}
      {error && (
        <div className="mx-4 md:mx-6 mt-4 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
          <i className="ri-error-warning-line text-base shrink-0" />
          <span className="flex-1 text-xs md:text-sm">{error}</span>
          <button
            onClick={() => setError(null)}
            className="shrink-0 text-red-400 hover:text-red-600 cursor-pointer"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 md:py-6">
        {/* History Panel */}
        {showHistory && (
          <div className="mb-6 bg-white rounded-2xl border border-stone-100 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center rounded-md bg-stone-100">
                  <i className="ri-history-line text-stone-500 text-xs" />
                </div>
                <h2 className="text-sm font-bold text-stone-900">Saved Posts</h2>
                <span className="text-xs text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full font-semibold">
                  {history.length}
                </span>
              </div>
              <button
                onClick={() => setShowHistory(false)}
                className="text-stone-400 hover:text-stone-600 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="px-4 py-4 max-h-80 overflow-y-auto">
              <SocialPostHistoryPanel
                history={history}
                loading={historyLoading}
                onLoad={handleLoadFromHistory}
                onDelete={deletePost}
              />
            </div>
          </div>
        )}

        <div className="mb-5 rounded-2xl border border-stone-100 bg-white overflow-hidden">
          <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-sm font-bold text-stone-900 flex items-center gap-2">
                <i className="ri-bookmark-3-line text-orange-400 text-base" />
                Social Presets
              </h2>
              <p className="text-xs text-stone-400 mt-0.5">Reuse high-performing identity, style, platform, and post structure</p>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowSavePreset((open) => !open);
                setPresetMessage(null);
              }}
              className="shrink-0 flex items-center gap-1.5 rounded-xl border border-stone-200 bg-stone-50 px-3 py-2 text-xs font-bold text-stone-600 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-600 transition-all cursor-pointer"
            >
              <i className="ri-add-line" />
              Save
            </button>
          </div>
          <div className="px-5 py-4 space-y-3">
            <div className="flex flex-col sm:flex-row gap-2">
              <select
                value={selectedPresetId}
                onChange={(event) => setSelectedPresetId(event.target.value)}
                disabled={presetsLoading || socialPresets.length === 0}
                className="flex-1 h-11 rounded-xl border border-stone-200 bg-stone-50 px-3 text-sm font-semibold text-stone-800 disabled:text-stone-400 focus:border-orange-300 focus:ring-4 focus:ring-orange-100 outline-none"
              >
                <option value="">
                  {presetsLoading
                    ? 'Loading presets...'
                    : socialPresets.length === 0
                      ? 'No saved social presets yet'
                      : 'Choose a saved social preset...'}
                </option>
                {socialPresets.map((preset) => (
                  <option key={preset.id} value={preset.id}>{preset.name}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => selectedPreset && applyPreset(selectedPreset)}
                disabled={!selectedPreset}
                className={`shrink-0 flex items-center justify-center gap-1.5 rounded-xl px-4 py-3 text-xs font-bold transition-all ${
                  selectedPreset
                    ? 'bg-stone-900 text-white hover:bg-stone-800 cursor-pointer'
                    : 'bg-stone-100 text-stone-400 cursor-not-allowed'
                }`}
              >
                <i className="ri-download-cloud-2-line" />
                Apply
              </button>
            </div>

            {selectedPreset && (
              <div className="flex items-center justify-between gap-3 rounded-xl border border-stone-200 bg-stone-50 px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-xs font-bold text-stone-800">{selectedPreset.name}</p>
                  <p className="truncate text-xs text-stone-400">
                    {selectedPreset.settings.postMode === 'daily' ? 'Daily content' : 'Campaign post'}
                    {' · '}
                    {String(
                      selectedPreset.settings.postMode === 'daily'
                        ? selectedPreset.settings.dailyForm?.platform ?? 'Any platform'
                        : selectedPreset.settings.campaignForm?.platform ?? 'Any platform'
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    await deletePreset(selectedPreset.id);
                    setSelectedPresetId('');
                    setPresetMessage(`Deleted "${selectedPreset.name}".`);
                  }}
                  className="shrink-0 flex items-center justify-center w-8 h-8 rounded-lg text-stone-400 hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer"
                  title="Delete preset"
                >
                  <i className="ri-delete-bin-line" />
                </button>
              </div>
            )}

            {showSavePreset && (
              <div className="rounded-xl border border-orange-100 bg-orange-50 p-3 space-y-2">
                <input
                  type="text"
                  value={presetName}
                  onChange={(event) => setPresetName(event.target.value.slice(0, 60))}
                  placeholder="Preset name, e.g. Fitness hot take style"
                  className="w-full rounded-xl border border-orange-200 bg-white px-3 py-2.5 text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400"
                />
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={savePreset}
                    disabled={savingPreset}
                    className="flex items-center justify-center gap-1.5 rounded-xl bg-orange-500 px-3 py-2.5 text-xs font-extrabold text-white hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <i className={savingPreset ? 'ri-loader-4-line animate-spin' : 'ri-save-3-line'} />
                    {savingPreset ? 'Saving...' : 'Save Preset'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSavePreset(false);
                      setPresetName('');
                      setPresetMessage(null);
                    }}
                    className="rounded-xl border border-stone-200 bg-white px-3 py-2.5 text-xs font-bold text-stone-500 hover:border-stone-300 hover:text-stone-700 cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {(presetMessage || presetsError) && (
              <p className={`text-xs ${presetsError ? 'text-red-500' : 'text-stone-500'}`}>
                {presetsError || presetMessage}
              </p>
            )}
          </div>
        </div>

        {/* ── Mode Selector ── */}
        <div className="mb-5">
          <div className="inline-flex items-center bg-stone-100 rounded-2xl p-1 gap-1">
            <button
              onClick={() => handleModeSwitch('campaign')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                postMode === 'campaign'
                  ? 'bg-white text-stone-900 shadow-sm'
                  : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              <i className={`ri-megaphone-line text-sm ${postMode === 'campaign' ? 'text-orange-500' : ''}`} />
              Campaign Post
            </button>
            <button
              onClick={() => handleModeSwitch('daily')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                postMode === 'daily'
                  ? 'bg-white text-stone-900 shadow-sm'
                  : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              <i className={`ri-calendar-2-line text-sm ${postMode === 'daily' ? 'text-emerald-500' : ''}`} />
              Daily Content
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold leading-none">
                New
              </span>
            </button>
          </div>
          {postMode === 'daily' && (
            <p className="text-xs text-stone-400 mt-2 ml-1">
              Generate daily social content even when you&apos;re not running a promotion
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: Form */}
          <div className="lg:col-span-2">
            {postMode === 'campaign' ? (
              <SocialPostForm
                profiles={profiles}
                profilesLoading={profilesLoading}
                formData={campaignForm}
                onChange={setCampaignForm}
                onGenerate={handleCampaignGenerate}
                onStopGeneration={stopGeneration}
                isGenerating={isGenerating}
                creditsRemaining={creditsRemaining}
                hasEnoughCredits={hasEnoughCredits}
                selectedProfileId={selectedProfileId}
                onProfileSelect={setSelectedProfileId}
              />
            ) : (
              <DailyContentForm
                profiles={profiles}
                profilesLoading={profilesLoading}
                formData={dailyForm}
                onChange={setDailyForm}
                onGenerate={handleDailyGenerate}
                onStopGeneration={stopGeneration}
                isGenerating={isGenerating}
                creditsRemaining={creditsRemaining}
                hasEnoughCredits={hasEnoughCredits}
                selectedProfileId={dailySelectedProfileId}
                onProfileSelect={setDailySelectedProfileId}
              />
            )}
          </div>

          {/* Right: Results */}
          <div className="lg:col-span-3">
            {postMode === 'campaign' ? (
              <SocialPostResults
                result={campaignResult}
                isGenerating={isGenerating}
                platform={campaignForm.platform}
                businessName={campaignForm.businessName || 'Your Business'}
                imageStyle={campaignForm.imageStyle}
                onRegenerate={handleCampaignGenerate}
              />
            ) : (
              <DailyContentResults
                result={dailyResult}
                isGenerating={isGenerating}
                platform={dailyForm.platform}
                quantity={dailyForm.quantity}
                onRegenerate={handleDailyGenerate}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
